import { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, Building2 } from 'lucide-react';
import api from '../../lib/api';
import type { OrganizationSearchResult } from '../../types';

// US States for filter dropdown
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

interface OrganizationTypeaheadProps {
  value: OrganizationSearchResult | null;
  onChange: (org: OrganizationSearchResult | null) => void;
  placeholder?: string;
  disabled?: boolean;
  showStateFilter?: boolean;
  excludeIds?: string[]; // Organizations to exclude from results (already linked)
}

/**
 * Typeahead search component for organizations
 */
export const OrganizationTypeahead = ({
  value,
  onChange,
  placeholder = 'Search organizations...',
  disabled = false,
  showStateFilter = false,
  excludeIds = [],
}: OrganizationTypeaheadProps) => {
  const [inputValue, setInputValue] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [results, setResults] = useState<OrganizationSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update input value when external value changes
  useEffect(() => {
    if (value) {
      setInputValue(value.name);
    } else {
      setInputValue('');
    }
  }, [value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  const doSearch = async (query: string, state: string) => {
    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('q', query);
      params.append('limit', '15');
      if (state) {
        params.append('state', state);
      }

      const response = await api.get(`/api/organizations/search?${params.toString()}`);

      // Filter out excluded organizations
      let orgs = response.data.organizations as OrganizationSearchResult[];
      if (excludeIds.length > 0) {
        orgs = orgs.filter((org) => !excludeIds.includes(org.id));
      }

      setResults(orgs);
      setIsOpen(true);
    } catch (err: any) {
      console.error('Organization search failed:', err);
      setError('Search failed');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Clear selection if user is typing something different
    if (value && newValue !== value.name) {
      onChange(null);
    }

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce search
    debounceRef.current = setTimeout(() => {
      doSearch(newValue, stateFilter);
    }, 300);
  };

  // Handle state filter change
  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newState = e.target.value;
    setStateFilter(newState);

    // Re-search if we have enough characters
    if (inputValue.length >= 2) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        doSearch(inputValue, newState);
      }, 300);
    }
  };

  // Handle selection
  const handleSelect = (org: OrganizationSearchResult) => {
    onChange(org);
    setInputValue(org.name);
    setIsOpen(false);
    setResults([]);
  };

  // Handle clear
  const handleClear = () => {
    onChange(null);
    setInputValue('');
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  // Handle focus
  const handleFocus = () => {
    if (inputValue.length >= 2 && results.length > 0) {
      setIsOpen(true);
    }
  };

  return (
    <div className="relative">
      {/* Input row */}
      <div className="flex gap-2">
        {/* State filter (optional) */}
        {showStateFilter && (
          <select
            value={stateFilter}
            onChange={handleStateChange}
            disabled={disabled}
            className="input w-24 flex-shrink-0"
          >
            <option value="">All</option>
            {US_STATES.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        )}

        {/* Search input */}
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {isLoading ? (
              <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
            ) : (
              <Search className="h-4 w-4 text-gray-400" />
            )}
          </div>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            placeholder={placeholder}
            disabled={disabled}
            className="input pl-10 pr-10"
          />
          {(inputValue || value) && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {error ? (
            <div className="p-3 text-sm text-red-600 dark:text-red-400">{error}</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-sm text-gray-500 dark:text-gray-400">
              {inputValue.length < 2
                ? 'Type at least 2 characters to search'
                : 'No organizations found'}
            </div>
          ) : (
            <ul>
              {results.map((org) => (
                <li key={org.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(org)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-start gap-3"
                  >
                    <Building2 className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {org.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {[org.city, org.state].filter(Boolean).join(', ') || 'Location unknown'}
                        {org.isPrivate && (
                          <span className="ml-2 text-xs text-purple-600 dark:text-purple-400">Private</span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Selected indicator */}
      {value && !isOpen && (
        <p className="mt-1 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
          <Building2 className="h-3 w-3" />
          Selected: {value.name}
          {value.city && value.state && ` (${value.city}, ${value.state})`}
        </p>
      )}
    </div>
  );
};
